import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import LogoMark from "./LogoMark";
import { scrollToId } from "../lib/scroll";
import { PHONE_TEL, waLink } from "../site";
import { EASE } from "./Reveal";

const LINKS = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "achievements", label: "Achievements" },
    { id: "training", label: "Training" },
    { id: "coaching", label: "Coaching" },
    { id: "journey", label: "Journey" },
    { id: "contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, id) => {
        e.preventDefault();
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 300 : 0);
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-[#D4A017]/15 bg-[#050505]/80 backdrop-blur-xl"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
                <a
                    href="#home"
                    data-testid="nav-logo"
                    className="flex items-center gap-3"
                    onClick={(e) => go(e, "home")}
                >
                    <LogoMark size={42} />
                    <span className="font-display text-2xl tracking-[0.18em] text-white">
                        ARYAN <span className="text-gold-grad">YOGA</span>
                    </span>
                </a>

                <nav className="hidden items-center gap-7 lg:flex">
                    {LINKS.map((l) => (
                        <a
                            key={l.id}
                            href={`#${l.id}`}
                            data-testid={`nav-link-${l.id}`}
                            onClick={(e) => go(e, l.id)}
                            className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/65 transition-colors duration-300 hover:text-[#F5C542]"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="#contact"
                        data-testid="nav-cta"
                        onClick={(e) => go(e, "contact")}
                        className="btn-gold hidden !px-6 !py-2.5 sm:inline-flex"
                    >
                        Start Training
                    </a>
                    <button
                        data-testid="nav-hamburger"
                        aria-label="Toggle menu"
                        onClick={() => setOpen((v) => !v)}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D4A017]/30 text-[#F5C542] lg:hidden"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        className="fixed inset-0 top-[72px] z-40 flex flex-col justify-between bg-[#050505]/97 px-8 pb-10 pt-8 backdrop-blur-2xl lg:hidden"
                        initial={{ opacity: 0, y: -16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.35, ease: EASE }}
                    >
                        <nav className="flex flex-col gap-2">
                            {LINKS.map((l, i) => (
                                <motion.a
                                    key={l.id}
                                    href={`#${l.id}`}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    onClick={(e) => go(e, l.id)}
                                    className="border-b border-white/5 py-3 font-display text-4xl tracking-wide text-white/85 transition-colors hover:text-[#F5C542]"
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i, duration: 0.45, ease: EASE }}
                                >
                                    {l.label}
                                </motion.a>
                            ))}
                        </nav>
                        <div className="flex items-center gap-4">
                            <a
                                href={PHONE_TEL}
                                data-testid="mobile-call-button"
                                className="btn-ghost flex-1 !px-4"
                            >
                                <Phone size={15} /> Call
                            </a>
                            <a
                                href={waLink("Namaste Trainer Aryan, I would like to start training.")}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="mobile-whatsapp-button"
                                className="btn-gold flex-1 !px-4"
                            >
                                <MessageCircle size={15} /> WhatsApp
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
